import Toast from 'react-native-toast-message';
import axios, {CancelTokenSource} from 'axios';
import {makeAutoObservable, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';

import {ERROR_MESSAGE} from '../components/ToastBottom';
import config from '../config';
import {DAYS_OF_THE_WEEK} from '../constants/days';
import {AppRoute} from '../models/AppRoute';
import {GeocodedSuburb, Shop} from '../models/middleware';
import {StoreType} from '../navigation/routes/types';
import apiService from '../transport/apiService';
import {
  getValueFromStorage,
  removeValueFromStorage,
  setValueToStorage,
} from '../utils/localStorage';
import {getStoreType} from '../utils/store';

import type {RootStore} from './rootStore';

const SHOP_SALESFORCE_STORAGE_KEY = 'SHOP_SALESFORCE_STORAGE_KEY';
const LIQUOR_SHOP_SALESFORCE_STORAGE_KEY = 'LIQUOR_SHOP_SALESFORCE_STORAGE_KEY';

const DEFAULT_MAX_DISTANCE_KM = 200;

// Poor man's request queue
let reqCount = 0;
let cancelToken: CancelTokenSource;

export class ShopSelectionStore {
  rootStore: RootStore;
  // If the user used a deep link like http://localhost:8080/store/C000130552,
  // shopIdSetInDeepLink is set to the shop/store id (C000130552 in this example)
  shopIdSetInDeepLink: string | null = null;
  shopSalesforceId: string | null = null;
  previouslySelectedStore: Shop | null = null;
  selectedStore: Shop | null = null;
  canPickup = false;
  canDeliver = false;
  private _loyaltyEnabled = false;
  selectedSuburb: GeocodedSuburb | null | undefined = null;
  suburbList: GeocodedSuburb[] = [];
  storeList: Shop[] = [];
  maxDistance = DEFAULT_MAX_DISTANCE_KM; // km
  liquorLicense = null;
  storeListLoading = true;
  // Can they create orders etc...
  ecommerceDisabled = false;
  // Looking up geocoding
  geocoding = false;
  storeLoading = true;
  suburbListLoading = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    makeLoggable(this);
  }

  get loyaltyEnabled() {
    /**
     * Dev shops need to be hardcoded because they are not in airtable where loyalty enabled info is.
     */
    const hardcodedLoyaltyEnabledShops = [
      // App development shop sydney
      '5dd328e4ec28667bb1004515',
      // App development shop brisbane
      '5dd32850ec28667ba8004a24',
      // App development Harry Brown
      '67073ba596dfb2d5271b92f5',
      // App development SnS
      '6707e67efc85738000abc378',
      // App development TC WA Shop
      '670739a52ff84b32dbe5c7c2',
    ];

    return (
      (this.selectedStore?.id &&
        hardcodedLoyaltyEnabledShops.includes(this.selectedStore.id)) ||
      this._loyaltyEnabled
    );
  }

  reset = () => {};

  setLoading = (value: boolean) => (this.storeLoading = value);

  clearShopSalesforceIdFromStorage = () =>
    removeValueFromStorage(SHOP_SALESFORCE_STORAGE_KEY);

  clearLiquorShopSalesforceIdFromStorage = () =>
    removeValueFromStorage(LIQUOR_SHOP_SALESFORCE_STORAGE_KEY);

  loadShopSalesforceIdFromLocalStorage = async () =>
    getValueFromStorage(SHOP_SALESFORCE_STORAGE_KEY);

  setShopSalesforceIdToStorage = (salesforceId: any) => {
    if (salesforceId) {
      setValueToStorage(SHOP_SALESFORCE_STORAGE_KEY, salesforceId);
    } else {
      this.clearShopSalesforceIdFromStorage();
    }
  };

  loadLiquorShopSalesforceIdFromLocalStorage = async () =>
    getValueFromStorage(LIQUOR_SHOP_SALESFORCE_STORAGE_KEY);

  setLiquorShopSalesforceIdToStorage = (salesforceId: any) => {
    if (salesforceId) {
      setValueToStorage(LIQUOR_SHOP_SALESFORCE_STORAGE_KEY, salesforceId);
    } else {
      this.clearLiquorShopSalesforceIdFromStorage();
    }
  };

  findSalesforceShop = async (shopSFId: string) => {
    let isDarkStore = false;

    try {
      // We have a salesforce ID for the store that the user has previously selected.
      // retrieve the shop from MFL using the salesforce ID
      try {
        const stores = await apiService.findSalesforceMatch(shopSFId);
        if (stores.length === 1) {
          // MFL has a match, check the capabilities of the store and
          // pass on to store chooser.
          const mflShop = stores[0];
          apiService.setShopId(mflShop.id);
          apiService.getOpenDeliveryTimes().then((deliveries: any) => {
            if (deliveries.length) runInAction(() => (this.canDeliver = true));
          });
          apiService.getOpenPickupTimes().then((pickups: any) => {
            if (pickups.length) runInAction(() => (this.canPickup = true));
          });
          this.selectStoreFromLoad(shopSFId, mflShop);
        } else isDarkStore = true;
      } catch (error: any) {
        if (error?.response?.status === 404) isDarkStore = true;
        else throw error;
      }

      if (isDarkStore) {
        // MFL does not have a match! Defer to darksite load.
        // Load the matching store from Airtable Salesforce Members Data
        const darksiteStore = await apiService.searchShopBySalesforceId(
          shopSFId,
        );
        await this.selectDarksiteStore(darksiteStore);
      }
    } catch (error: any) {
      console.log(
        'There was a problem retrieving the store details',
        error.code,
        error,
      );
      runInAction(() => (this.storeLoading = false));
    }
  };

  // Storage calls
  // This is loading a shop when the user is opening the app
  // and they've already selected a store. We only store the
  // salesforce ID, so we retrieve the store details from MFL.
  retrieveShopData = async () => {
    try {
      const shopSFId = await this.loadShopSalesforceIdFromLocalStorage();
      if (shopSFId) {
        runInAction(() => {
          this.shopSalesforceId = shopSFId;
        });
        // We have a salesforce ID for the store that the user has previously selected.
        // retrieve the shop from MFL using the salesforce ID
        await this.findSalesforceShop(shopSFId);
      } else {
        runInAction(() => (this.storeLoading = false));
      }
    } catch (error: any) {
      console.log(
        'There was a problem retrieving the store details from storage',
        error.code,
        error,
      );
      runInAction(() => (this.storeLoading = false));
    }
  };

  reverseGeocode = async (lat: number, lon: number) => {
    runInAction(() => (this.geocoding = true));
    try {
      const suburbs = await apiService.reverseGeocode(lat, lon);
      runInAction(() => {
        console.log('👾[shopSelectionStore]', 'reverseGeocode', suburbs);
        this.suburbList = suburbs;
        this.geocoding = false;
        // if (suburbs.length === 1) {
        //   //  Exactly 1 suburb returned, select that.
        //   runInAction(() => {
        //     this.resetLoading();
        //     this.selectSuburb(suburbs[0]);
        //   });
        // }
      });
      return suburbs;
    } catch (err) {
      console.log('errored while reverse geocoding', err);
      return [];
    }
  };

  async loadSuburbList(searchText: string) {
    runInAction(() => {
      this.suburbListLoading = true;
      this.maxDistance = DEFAULT_MAX_DISTANCE_KM;
    });

    let reqIndex = ++reqCount;
    try {
      //Check if there are any previous pending requests
      if (typeof cancelToken !== typeof undefined) {
        cancelToken.cancel('Operation canceled due to new request.');
      }

      //Save the cancel token for the current request
      cancelToken = axios.CancelToken.source();

      if (!!searchText && searchText.length >= 3) {
        const res = await apiService.searchSuburbs(searchText, cancelToken);
        if (!!res && res.length > 0 && reqIndex === reqCount) {
          runInAction(() => {
            this.suburbList = res;
          });
        }
      } else {
        this.suburbList = [];
      }
    } catch (err) {
      console.log(err);
    } finally {
      if (reqIndex >= reqCount) {
        runInAction(() => (this.suburbListLoading = false));
      }
    }
  }

  async querySuburbStores(suburb: GeocodedSuburb, maxDistance: number) {
    if (!suburb?.locality || !suburb?.lat || !suburb?.lon) {
      throw new Error('Suburb lat/lon not found');
    }

    const postcode = suburb?.postal_code;
    const suburbName = suburb?.locality;

    let shopList: Shop[];

    // Dev needs to load shops directly from MFL
    if (this.rootStore.settingsStore.showAppDevelopmentShop) {
      shopList = await apiService.getShopsFromMFL();
      // ⚠️ Assuming DEV stores can delivery and pickup
      shopList.forEach(shop => {
        shop.pickup_available = true;
        shop.delivery_available = true;
      });
    } else {
      // The shops in this list have already set their pickup and delivery
      // capabilities, `pickup_available` and `delivery_available`
      // (delivery_available as interpreted as "can deliver to this suburb (suburbName)").
      shopList = await apiService.searchShopsBySuburbPostcodeLatLon(
        suburbName,
        postcode,
        suburb.lat,
        suburb.lon,
        maxDistance,
      );
    }

    return shopList;
  }

  async selectSuburb(suburb?: GeocodedSuburb | null) {
    if (suburb) {
      this.selectedSuburb = suburb;
    }
    // @ts-ignore
    suburb = suburb || {...this.selectedSuburb};
    if (!suburb) {
      throw new Error('Suburb is required');
    }

    let shopList: Shop[];
    try {
      shopList = await this.querySuburbStores(suburb, this.maxDistance);

      runInAction(() => {
        this.storeList = shopList;
        this.storeListLoading = false;
      });
    } catch (error) {
      console.log('Something went wrong!', error);
    }
  }

  expandSearchRange() {
    runInAction(() => {
      this.maxDistance = this.maxDistance + DEFAULT_MAX_DISTANCE_KM;
    });
  }

  clearSuburbList() {
    runInAction(() => {
      this.suburbList = [];
    });
  }

  loadLiquorLicense = async () => {
    try {
      const data = await apiService.getLiquorLicense(this.shopSalesforceId);
      runInAction(() => {
        this.liquorLicense = data || null;
      });
    } catch (error) {
      console.log('Unable to retrieve liquor license', error);
    }
  };

  resetLoading() {
    runInAction(() => (this.storeListLoading = true));
  }

  isStoreListLoading() {
    return this.storeListLoading;
  }

  async selectPreviousStore() {
    if (this.previouslySelectedStore) {
      const previousStore = {...this.previouslySelectedStore};
      if (this.rootStore.settingsStore.showAppDevelopmentShop) {
        previousStore.pickup_available = true;
        previousStore.delivery_available = true;
      }
      await this.selectStore(previousStore);
    }
    // else, suburb/store selector
  }

  async selectStore(store: Shop) {
    this.rootStore.globalStore.setIsSwitchingStore(true);
    const previousStore = this.selectedStore;

    runInAction(() => {
      this.previouslySelectedStore = previousStore;
      this.storeLoading = true;
      this.maxDistance = DEFAULT_MAX_DISTANCE_KM;
    });

    if (!store.store_number) {
      console.log('ERROR, shop has no Store Number', store);
      return;
    }
    try {
      // In store shopping only, redirect to darksite
      if (!store.delivery_available && !store.pickup_available) {
        this.selectDarksiteStore(store);
      } else {
        // Coming in from the store chooser, need to look up the mfl shop
        const stores = await apiService.findSalesforceMatch(store.store_number);
        if (stores.length === 0) {
          throw new Error(
            `No stores returned by eCommerce for ${store?.store_number}`,
          );
        }

        apiService.setShopId(stores[0].id);
        apiService.clearDepartment();

        runInAction(() => {
          this.setShopSalesforceIdToStorage(store.store_number);
          this.shopSalesforceId = store.store_number;
          this.selectedStore = stores[0];

          this.ecommerceDisabled = false;
          this.rootStore.globalStore.acceptedInstoreShopping(false);
          this.canDeliver = store.delivery_available;
          this.canPickup = store.pickup_available;
          this._loyaltyEnabled = store.loyalty_enabled;
          this.storeLoading = false;
        });
      }
      await this.rootStore.initialiseSelectedStoreData();
      this.rootStore.globalStore.setIsSwitchingStore(false);
      this.rootStore.resetOffersStore(); // Offers can be store specific
    } catch (error: any) {
      console.debug('Select store error', error);
      Toast.show({
        type: ERROR_MESSAGE,
        text1: `There was a problem setting your store to ${store?.name}. Please try again.`,
        visibilityTime: 10000,
      });
    }
  }

  // App reload, loading store from local storage cues
  selectStoreFromLoad(storeId: string | null, mflShop: Shop) {
    runInAction(() => {
      this.setShopSalesforceIdToStorage(storeId);
      this.shopSalesforceId = storeId;
      this.selectedStore = mflShop;
      this.ecommerceDisabled = false;
      this._loyaltyEnabled = mflShop.loyalty_enabled;
      this.storeLoading = false;
    });
  }

  selectDarksiteStore = async (store: Shop) => {
    try {
      const darksiteId = await apiService.getDarksiteId(store.store_number);
      const stores = await apiService.findSalesforceMatch(darksiteId);

      // Make sure the darksite store is active in MFL
      if (stores.length === 0) {
        throw new Error(
          `No stores returned by eCommerce for ${store?.store_number}`,
        );
      }

      const regularHours: any = {};
      DAYS_OF_THE_WEEK.forEach(day => {
        const from = store.regular_hours?.[day].from;
        const until = store.regular_hours?.[day].until;
        if (from && until) {
          regularHours[day] = {
            from,
            until,
          };
        }
      });
      const munged = {
        ...stores[0],
        ...store,
        id: stores[0].id,
      };

      runInAction(() => {
        this.setShopSalesforceIdToStorage(store.store_number);
        this.shopSalesforceId = store.store_number;
        this._loyaltyEnabled = store.loyalty_enabled;
        this.selectedStore = munged;
        apiService.setShopId(munged.id);
        apiService.clearDepartment();
        this.ecommerceDisabled = true;
        this.rootStore.globalStore.acceptedInstoreShopping(false);
        this.storeLoading = false;
      });
    } catch (error: any) {
      console.debug('Airtable darksite err', error);
      runInAction(() => {
        this.shopSalesforceId = null;
        this.storeLoading = false;
      });
      throw error;
    }
  };

  setCanDeliver = (value: boolean) => (this.canDeliver = value);

  setCanPickup = (value: boolean) => (this.canPickup = value);

  setShopIdSetInDeepLink = (value: string | null) =>
    (this.shopIdSetInDeepLink = value);

  // Util
  getSelectedStoreType = () => getStoreType(this.selectedStore);

  isSelectedStoreGiftCardStore = () =>
    this.getSelectedStoreType() === StoreType.GiftCardStore;

  getHomeAppRoute = () =>
    this.isSelectedStoreGiftCardStore()
      ? AppRoute.GIFT_CARDS_MAIN
      : AppRoute.HOME;

  getSelectedStoreSummary = () => {
    if (!this.shopSalesforceId || !this.selectedStore) {
      return null;
    }
    return {
      id: this.shopSalesforceId,
      name: this.selectedStore.name,
    };
  };
}
